import { Box, Button, Flex, Spacer, Stack } from '@chakra-ui/react'
import React, { memo } from 'react'

import { SHWError } from '@/common/error'

import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import { trackEvent } from '@/client/services/analytics'

import Form from '@/components/UI/Form/Form'
import type { AlignInputs, FormValues } from '@/components/UI/Form/types'
import { NAVBAR_MOBILE_HEIGHT, NAVBAR_WIDTH } from '@/components/Worlds/UI/constants'

import { FormWizardV2Context } from './context'
import FormWizardV2ChecklistHorizontal from './FormWizardV2ChecklistHorizontal'
import FormWizardV2ChecklistHorizontalStepper from './FormWizardV2ChecklistHorizontalStepper'
import FormWizardV2ChecklistVertical from './FormWizardV2ChecklistVertical'
import { isStepDisabled } from './helpers'
import type { FormWizardV2ContextType, FormWizardV2Step, FormWizardV2Values } from './types'

type FormWizardChecklistType = 'vertical' | 'horizontal' | 'horizontal-stepper'

/**
 * FormWizardV2 is an evolution of FormWizard component. It aims to cut down on excessive
 * re-renders and to keep element refs stable (so user doesn't lose focus and/or touch/drag).
 * Requirements:
 * - The number of steps, their order and their slugs must never change.
 * - The components MUST have a stable ref to `ContainerComponent` (useMemo it and avoid changing the ref).
 * Features:
 * - The steps are allowed to change `formInputs`, `onValidate` and `title` without losing state.
 */

// NOTE: Do NOT use `useMemo` with steps as dependency internally in this component.
// This component MUST be able to withstand the changes to steps without losing state.
// The only thing that should break this component is a change to `slug` or the steps disappearing.
function FormWizardV2<T extends string>({
  name,
  steps,
  onCancel,
  onStepChange,
  onSubmit,
  checklistType = 'horizontal',
  alignInputs,
  enableAnalyticsTracking = false,
  footerPosition = 'relative',
  excludeNavbarPadding = false,
  maxWidth,
  submitButtonText = 'Submit',
}: {
  name: string
  steps: FormWizardV2Step<T>[]
  onCancel?: null | (() => void)
  onStepChange?: (slug: T, values?: FormWizardV2Values<T>) => void
  onSubmit: (values: FormWizardV2Values<T>) => void | Promise<void>
  checklistType?: FormWizardChecklistType
  alignInputs?: AlignInputs
  enableAnalyticsTracking?: boolean
  footerPosition?: 'fixed' | 'fixed-in-modal' | 'relative'
  excludeNavbarPadding?: boolean
  maxWidth: string
  submitButtonText?: string
}): JSX.Element {
  if (steps.length === 0) {
    throw new Error('Invalid usage: FormWizard needs at least one step to work with')
  }

  const user = useUser()
  const appContext = useAppContext()

  // This variable "stepSlugs" means to provide a stable slugs value to the rest
  // of the component. Since one of the constraints of this component is that the
  // step and their slugs never change, even tho everything else may, we can use this
  // variable instead of "steps" variable in dependencies below and it will be a stable ref.
  const stepSlugs = React.useMemo(
    () => steps.map(step => step.slug),
    // oxlint-ignore-next-line react/exhaustive-deps
    [steps.map(step => step.slug).join('-')]
  )

  const wizardValuesInitial = React.useMemo(() => {
    const values = {} as FormWizardV2Values<T>
    // ^ Override the inferred type because we're filling the values later
    stepSlugs.forEach(item => {
      values[item] = null
    })
    return values
  }, [stepSlugs])
  const wizardValuesRef = React.useRef(wizardValuesInitial)

  const [wantedStepSlug, setWantedStepSlug] = React.useState<T>(steps[0].slug)

  // tl;dr This memo finds the first non-disabled step starting at wantedStep.
  // This allows us to skip steps that are disabled as a result of the onComplete callback, e.g. if a particular response renders some future steps unnecessary.
  // This skipping logic lives here instead of inside onSubmit because the certain steps can become disabled in the onComplete handler
  const [currentStepIndex, currentStep] = React.useMemo(() => {
    const wantedStepIndex = steps.findIndex(step => step.slug === wantedStepSlug)
    const foundStepIndex = steps.findIndex(
      (step, i) => i >= wantedStepIndex && !isStepDisabled(step)
    )
    if (foundStepIndex === -1) {
      throw new SHWError({
        internalMessage: 'FormWizard: unable to find a next step that is not disabled.',
      })
    }
    return [foundStepIndex, steps[foundStepIndex]]
  }, [steps, wantedStepSlug])

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      if (enableAnalyticsTracking) {
        trackEvent({
          category: `[FormWizard] ${name}`,
          action: `${currentStep.slug} - Submit`,
        })
      }

      // Update the wizard values
      wizardValuesRef.current[currentStep.slug] = values

      // Invoke the onComplete() callback if it exists
      currentStep?.onComplete?.(values)

      // If there's a next step, go to it
      for (
        let nextStepIndex = currentStepIndex + 1;
        nextStepIndex < steps.length;
        nextStepIndex += 1
      ) {
        const nextStep = steps[nextStepIndex]
        if (!isStepDisabled(nextStep)) {
          onStepChange?.(nextStep.slug, wizardValuesRef.current)
          setWantedStepSlug(nextStep.slug)
          // Scroll to top of the page
          window.scrollTo({ top: 0 })
          return
        }
      }
      // Otherwise, submit the form
      await onSubmit({ ...wizardValuesRef.current })
    },
    [enableAnalyticsTracking, currentStep, currentStepIndex, steps, onSubmit, name, onStepChange]
  )

  // TODO: Should we support storing the partial state of the step? What if the form inputs change
  // between back and forth? Not supporting these for now.
  const handleBack = React.useCallback(() => {
    if (currentStepIndex === 0) {
      onCancel?.()
      return
    }

    // Go back to the first non-disabled step, preserving branching logic
    for (let i = currentStepIndex - 1; i >= 0; i -= 1) {
      if (!isStepDisabled(steps[i])) {
        setWantedStepSlug(steps[i].slug)
        onStepChange?.(steps[i].slug, wizardValuesRef.current)
        // Scroll to top of the page
        window.scrollTo({ top: 0 })
        return
      }
    }
  }, [currentStepIndex, onCancel, steps, onStepChange])

  const contextValue: FormWizardV2ContextType<T> = React.useMemo(
    () => ({
      valuesRef: wizardValuesRef,
      goBack: handleBack,
    }),
    [wizardValuesRef, handleBack]
  )

  const fixFooter = footerPosition === 'fixed' || footerPosition === 'fixed-in-modal'

  function getContentForStep(step: FormWizardV2Step<T>) {
    const { ContainerComponent, NextButtonComponent = Button } = step
    const stepIndex = steps.findIndex(item => item.slug === step.slug)
    const onFinalStep = stepIndex === steps.length - 1

    return (
      <Flex
        direction="column"
        align="center"
        flex={1}
        mr={{
          base: 0,
          lg: checklistType === 'horizontal' || checklistType === 'horizontal-stepper' ? 0 : '8.6%',
        }}
        ml={{
          base: 0,
          lg: checklistType === 'horizontal' || checklistType === 'horizontal-stepper' ? 0 : '32px',
        }}
        key={step.slug}
        width="100%"
      >
        <ContainerComponent title={step.title}>
          <Box pb={fixFooter ? '64px' : 0} width="100%">
            <Form
              name={`Wizard-${name}-Step-${step.slug}`}
              onSubmit={handleSubmit}
              blocks={step.formInputs.map(item => ({
                ...item,
                // Override initial value with one from local state if it exists
                initialValue: (wizardValuesRef.current[step.slug]?.[item.name] ??
                  ('initialValue' in item ? item.initialValue : null)) as never,
              }))}
              onValidate={step.onValidate}
              alignInputs={alignInputs}
              FooterComponent={({ submitting }) => (
                <Flex
                  justify="center"
                  position={
                    fixFooter
                      ? footerPosition === 'fixed-in-modal'
                        ? 'absolute'
                        : 'fixed'
                      : 'relative'
                  }
                  bottom={
                    fixFooter
                      ? footerPosition === 'fixed-in-modal'
                        ? 0
                        : { base: excludeNavbarPadding ? 0 : NAVBAR_MOBILE_HEIGHT, sm: 0 }
                      : undefined
                  }
                  left={fixFooter ? 0 : undefined}
                  right={fixFooter ? 0 : undefined}
                  mt={fixFooter ? 0 : '32px'}
                  borderTop={fixFooter ? '2px solid' : undefined}
                  borderTopColor={fixFooter ? 'border' : undefined}
                  bg={fixFooter ? 'bg' : undefined}
                  py={fixFooter ? '16px' : undefined}
                  pr={fixFooter ? '16px' : undefined}
                  pl={{
                    base: '16px',
                    sm:
                      user.isLoggedOut || excludeNavbarPadding
                        ? fixFooter
                          ? '16px'
                          : 0
                        : `calc(${NAVBAR_WIDTH} + 16px)`,
                  }}
                  width="100%"
                >
                  <Stack
                    direction="row"
                    spacing="16px"
                    justify="space-between"
                    width={maxWidth}
                    maxWidth="100%"
                  >
                    {stepIndex !== 0 || onCancel != null ? (
                      <Button
                        variant="outline"
                        isDisabled={submitting}
                        onClick={handleBack}
                        flex={{ base: 1, md: 0 }}
                      >
                        Back
                      </Button>
                    ) : (
                      <Spacer />
                    )}
                    {!onFinalStep && step.hideNextButton === true ? null : (
                      <NextButtonComponent
                        type="submit"
                        isLoading={submitting}
                        flex={{ base: 1, md: 'unset' }}
                      >
                        {onFinalStep ? submitButtonText : 'Next'}
                      </NextButtonComponent>
                    )}
                  </Stack>
                </Flex>
              )}
            />
          </Box>
        </ContainerComponent>
      </Flex>
    )
  }

  let content: React.ReactNode

  // Render all of the steps so we can scan their queries in SSR
  if (appContext.prepass) {
    content = steps.map(step => getContentForStep(step))
  } else {
    content = currentStep == null ? null : getContentForStep(currentStep)
  }

  content = (
    <FormWizardV2Context.Provider value={contextValue}>{content}</FormWizardV2Context.Provider>
  )

  if (checklistType === 'vertical') {
    return (
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        spacing={{
          base: 0,
          lg: '8.6%',
        }}
        width="100%"
      >
        {steps.length <= 1 ? null : (
          <FormWizardV2ChecklistVertical steps={steps} currentStep={currentStep} />
        )}
        {content}
      </Stack>
    )
  }

  return (
    <Flex direction="column" align="center" width="100%">
      <Stack
        direction={{ base: 'column', lg: 'column' }}
        spacing="32px"
        width={maxWidth}
        maxWidth="100%"
      >
        {steps.length > 1 && checklistType === 'horizontal-stepper' ? (
          <FormWizardV2ChecklistHorizontalStepper steps={steps} currentStep={currentStep} />
        ) : null}
        {steps.length > 1 && checklistType === 'horizontal' ? (
          <FormWizardV2ChecklistHorizontal steps={steps} currentStep={currentStep} />
        ) : null}
        {content}
      </Stack>
    </Flex>
  )
}

export default memo(FormWizardV2)
